import React, { useState, useEffect } from 'react';
import AdminPanel from '../admin/AdminPannel';

// --- Unicode Chess Piece Characters ---
// White: ♔ ♕ ♖ ♗ ♘ ♙
// Black: ♚ ♛ ♜ ♝ ♞ ♟

// Mapping piece string identifier to its Unicode character
const pieceUnicode = {
  wk: '♔', wq: '♕', wr: '♖', wb: '♗', wn: '♘', wp: '♙',
  bk: '♚', bq: '♛', br: '♜', bb: '♝', bn: '♞', bp: '♟',
};

const Chess = () => {
  const [board, setBoard] = useState(() => {
    const initialBoard = Array(8).fill(null).map(() => Array(8).fill(null));
    const blackPieces = ['br', 'bn', 'bb', 'bq', 'bk', 'bb', 'bn', 'br'];
    const whitePieces = ['wr', 'wn', 'wb', 'wq', 'wk', 'wb', 'wn', 'wr'];
    for (let i = 0; i < 8; i++) {
      initialBoard[0][i] = blackPieces[i];
      initialBoard[1][i] = 'bp';
      initialBoard[6][i] = 'wp';
      initialBoard[7][i] = whitePieces[i];
    }
    return initialBoard;
  });
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [turn, setTurn] = useState('white');
  const [gameStatus, setGameStatus] = useState(`${turn}'s Turn`);
  const [whitePlayerName, setWhitePlayerName] = useState('White');
  const [blackPlayerName, setBlackPlayerName] = useState('Black');
  const [moveAnimation, setMoveAnimation] = useState(null); // { from: [rank, file], to: [rank, file] }

  useEffect(() => {
    setGameStatus(`${turn === 'white' ? whitePlayerName : blackPlayerName}'s Turn`);
    // In a real game, you'd also check for check, checkmate, stalemate here
  }, [turn, whitePlayerName, blackPlayerName]);

  const boardStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 1fr)',
    gridTemplateRows: 'repeat(8, 1fr)',
    width: 'min(80vh, 80vw)',
    height: 'min(80vh, 80vw)',
    margin: '20px auto',
    border: '3px solid #444',
    boxSizing: 'border-box',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  };

  const squareStyle = (rank, file) => {
    const isLight = (rank + file) % 2 === 0;
    const backgroundColor = isLight ? '#f0d9b5' : '#b58863';
    const isSelected = selectedSquare && selectedSquare[0] === rank && selectedSquare[1] === file;
    const isMoveSource = moveAnimation && moveAnimation.from[0] === rank && moveAnimation.from[1] === file;
    const isMoveTarget = moveAnimation && moveAnimation.to[0] === rank && moveAnimation.to[1] === file;

    return {
      backgroundColor: isSelected ? '#ffff66' : (isMoveTarget ? '#aaffaa' : backgroundColor),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      fontSize: '2.5em',
      fontWeight: 'bold',
      boxSizing: 'border-box',
      userSelect: 'none',
      transition: 'background-color 0.3s ease, transform 0.3s ease',
      transform: isMoveSource ? 'scale(1.1)' : 'scale(1)',
      zIndex: isSelected || isMoveSource ? 10 : 1,
      borderRadius: '2px',
      '&:hover': {
        backgroundColor: isSelected ? '#ffff66' : (isLight ? '#e0c49b' : '#a57b53'),
      },
    };
  };

  const pieceStyle = (piece) => {
    const color = piece[0] === 'w' ? '#fff' : '#000';
    return {
      color,
      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
    };
  };

  const handleSquareClick = (rank, file) => {
    const clickedPiece = board[rank][file];

    if (selectedSquare === null) {
      if (clickedPiece && clickedPiece[0] === turn[0]) {
        setSelectedSquare([rank, file]);
      }
    } else {
      const [selectedRank, selectedFile] = selectedSquare;
      const pieceToMove = board[selectedRank][selectedFile];

      if (selectedRank === rank && selectedFile === file) {
        setSelectedSquare(null);
        return;
      }

      if (clickedPiece && clickedPiece[0] === turn[0]) {
        setSelectedSquare([rank, file]);
        return;
      }

      // Basic move execution (without full validation for brevity)
      if (pieceToMove) {
        const newBoard = board.map(row => [...row]);
        newBoard[rank][file] = pieceToMove;
        newBoard[selectedRank][selectedFile] = null;

        setMoveAnimation({ from: [selectedRank, selectedFile], to: [rank, file] });

        setTimeout(() => {
          setBoard(newBoard);
          setTurn(prevTurn => (prevTurn === 'white' ? 'black' : 'white'));
          setSelectedSquare(null);
          setMoveAnimation(null);
        }, 300); // Duration of the animation
      } else {
        setSelectedSquare(null);
      }
    }
  };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', textAlign: 'center', padding: '20px', backgroundColor: '#f4f4f4', minHeight: '100vh' }}>
      <AdminPanel />
      <h1 style={{ color: '#333', marginBottom: '20px' }}>Beautiful Animated Chess</h1>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '10px' }}>
        <div>
          <label htmlFor="whiteName" style={{ marginRight: '10px' }}>
            White Player:
          </label>
          <input
            type="text"
            id="whiteName"
            value={whitePlayerName}
            onChange={(e) => setWhitePlayerName(e.target.value)}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>
        <div>
          <label htmlFor="blackName" style={{ marginRight: '10px' }}>
            Black Player:
          </label>
          <input
            type="text"
            id="blackName"
            value={blackPlayerName}
            onChange={(e) => setBlackPlayerName(e.target.value)}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>
      </div>
      <div style={boardStyle}>
        {board.map((row, rank) => (
          row.map((piece, file) => (
            <div
              key={`${rank}-${file}`}
              style={squareStyle(rank, file)}
              onClick={() => handleSquareClick(rank, file)}
            >
              {piece && <span style={pieceStyle(piece)}>{pieceUnicode[piece]}</span>}
            </div>
          ))
        ))}
      </div>
      <div style={{ textAlign: 'center', fontSize: '1.2em', marginTop: '15px', color: '#555', fontWeight: 'bold' }}>
        {gameStatus}
      </div>
      <p style={{ fontSize: '0.9em', color: '#777', marginTop: '20px' }}>
        This is a basic animated chess game. Move validation is simplified for this example.
      </p>
    </div>
  );
};

export default Chess;